import { Icon } from "@iconify/react";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";

interface ClaimGuidanceCardProps {
  /** Name of the person who created this Dedicated Profile — omitted only if
   * that account has since been deleted; the copy below falls back gracefully. */
  creatorName: string | null;
  onDismiss: () => void;
  /** Present only where in-app navigation makes sense (the Dashboard, once
   * signed in) — omitted on the pre-signin verify-OTP screen. */
  onGoToCaregivers?: () => void;
}

/**
 * One-time "what to do next" guidance shown right after a Dedicated Profile
 * claim's OTP verifies, and again once on the first Dashboard visit after
 * signing in (see claimGuidance.ts) — this account now fully belongs to
 * whoever just verified it; the person who used to manage it has no access
 * at all until invited fresh, same as a stranger. Purely informational:
 * dismissing it doesn't change anything, it's a repeat of the same message,
 * not a two-step flow.
 */
export function ClaimGuidanceCard({ creatorName, onDismiss, onGoToCaregivers }: ClaimGuidanceCardProps) {
  const creatorLabel = creatorName || "the person who created this profile";

  return (
    <AppModal
      open
      onOpenChange={(open) => { if (!open) onDismiss(); }}
      width={480}
      hideCloseButton
      className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!p-8"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F17893]">
        <Icon icon="lucide:shield-check" className="h-6 w-6 text-white" />
      </div>

      <h2 className="mt-4 text-lg font-extrabold text-[#0A2947]">This account is now fully yours</h2>
      <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">
        {creatorLabel} no longer has access to your records. That access ended the moment you
        verified your email. If you'd like to give them (or anyone else) access again, you're in
        control of exactly what they can see.
      </p>

      <div className="mt-5 space-y-3 rounded-2xl bg-[#F9FAFB] p-4">
        <p className="text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">To invite someone as a caregiver</p>
        {[
          "Open the sidebar and go to Family & Caregivers",
          "Select Manage Caregivers",
          `Send an invite to ${creatorLabel} (or anyone else) and choose what they can see`,
        ].map((step, index) => (
          <div key={step} className="flex items-start gap-3">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F17893] text-[10px] font-extrabold text-white">
              {index + 1}
            </span>
            <p className="text-sm text-[#374151]">{step}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 text-xs text-[#9CA3AF]">
        You can change or fully revoke this access at any time from the same section.
      </p>

      <div className="mt-6 flex gap-3">
        <Button
          variant="outline"
          onClick={onDismiss}
          className="!h-auto !flex-1 !rounded-xl !border-2 !border-[#7B6D9B] !bg-white !py-3 !text-sm !font-extrabold !text-[#0A2947] hover:!border-[#7B6D9B] hover:!bg-[#F7F7F7] hover:!text-[#0A2947]"
        >
          Got it
        </Button>
        {onGoToCaregivers && (
          <Button
            onClick={onGoToCaregivers}
            className="!h-auto !flex-1 !rounded-xl !bg-[#F17893] !py-3 !text-sm !font-extrabold !text-white hover:!bg-[#E85D7C]"
          >
            Go now
          </Button>
        )}
      </div>
    </AppModal>
  );
}
